// The filter axes on the payroll table, beside the role chips: one dropdown per
// stage column (therapist approval, Basel, sent, bonus) and a pick-list for
// looking at a few people.
//
// All feed the SAME filtered array the tiles are computed from — a filter that only
// trims rows would leave the headline numbers describing a different population.

export type StatusFilter = 'all' | 'approved' | 'pending' | 'commented';
export type BaselFilter = 'all' | 'yes' | 'no';
export type SentFilter = 'all' | 'sent' | 'unsent';
export type BonusFilter = 'all' | 'eligible' | 'not' | 'na';

export const STATUS_LABEL: Record<StatusFilter, string> = {
  all: 'הכל',
  approved: 'אושר',
  pending: 'טרם אושר',
  commented: 'עם הערה',
};

// One dropdown = one column. Every option carries the column name — three filters
// whose chosen value reads just "אושר" are indistinguishable when closed.
export function ColumnFilter<T extends string>({ title, value, onChange, options }: {
  title: string;
  value: T;
  onChange: (v: T) => void;
  options: { v: T; label: string; count: number }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className={`text-sm border rounded-md px-3 py-1.5 bg-white ${
        value !== 'all' ? 'border-blue-400 text-blue-700' : 'border-gray-300'}`}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{title}: {o.label} ({o.count})</option>
      ))}
    </select>
  );
}

export function SalaryFilters({
  status, setStatus, counts,
  basel, setBasel, baselCounts,
  sent, setSent, sentCounts,
  bonus, setBonus, bonusCounts,
  names, picked, setPicked, open, setOpen,
}: {
  status: StatusFilter;
  setStatus: (s: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
  basel: BaselFilter;
  setBasel: (s: BaselFilter) => void;
  baselCounts: Record<BaselFilter, number>;
  sent: SentFilter;
  setSent: (s: SentFilter) => void;
  sentCounts: Record<SentFilter, number>;
  bonus: BonusFilter;
  setBonus: (s: BonusFilter) => void;
  bonusCounts: Record<BonusFilter, number>;
  names: { name: string; label: string }[];
  picked: string[];
  setPicked: (p: string[]) => void;
  open: boolean;
  setOpen: (o: boolean) => void;
}) {
  const toggle = (n: string) =>
    setPicked(picked.includes(n) ? picked.filter((x) => x !== n) : [...picked, n]);

  return (
    <>
      <ColumnFilter
        title="אישור באסל" value={basel} onChange={setBasel}
        options={[
          { v: 'all', label: 'הכל', count: baselCounts.all },
          { v: 'yes', label: 'אושר', count: baselCounts.yes },
          { v: 'no', label: 'ממתין', count: baselCounts.no },
        ]}
      />
      <ColumnFilter
        title="נשלח" value={sent} onChange={setSent}
        options={[
          { v: 'all', label: 'הכל', count: sentCounts.all },
          { v: 'sent', label: 'נשלח', count: sentCounts.sent },
          { v: 'unsent', label: 'טרם נשלח', count: sentCounts.unsent },
        ]}
      />
      <ColumnFilter
        title="אישור מטפל" value={status} onChange={setStatus}
        options={(Object.keys(STATUS_LABEL) as StatusFilter[]).map((k) => (
          { v: k, label: STATUS_LABEL[k], count: counts[k] }
        ))}
      />
      <ColumnFilter
        title="בונוס" value={bonus} onChange={setBonus}
        options={[
          { v: 'all', label: 'הכל', count: bonusCounts.all },
          { v: 'eligible', label: 'זכאי', count: bonusCounts.eligible },
          { v: 'not', label: 'לא זכאי', count: bonusCounts.not },
          { v: 'na', label: 'לא רלוונטי', count: bonusCounts.na },
        ]}
      />

      {/* A checkbox list rather than a multi-select: with 54 names, "which ones am I
          looking at" has to be readable at a glance, and clearing has to be one click. */}
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className={`text-sm border rounded-md px-3 py-1.5 bg-white ${
            picked.length ? 'border-blue-400 text-blue-700' : 'border-gray-300 text-gray-600'}`}
        >
          {picked.length ? `${picked.length} מטפלים נבחרו` : 'בחירת מטפלים'} ▾
        </button>
        {open && (
          <div className="absolute z-20 mt-1 w-72 max-h-72 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg p-2">
            <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-gray-100">
              <span className="text-xs text-gray-500">{names.length} בטבלה</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPicked([])}
                  disabled={!picked.length}
                  className="text-xs text-blue-700 hover:text-blue-900 disabled:text-gray-300"
                >
                  נקה
                </button>
                {/* Picking is multi-select, so the list cannot close itself on a
                    click — an explicit close beats hunting for the toggle button. */}
                <button
                  onClick={() => setOpen(false)}
                  className="text-xs rounded border border-gray-300 px-2 py-0.5 text-gray-700 hover:bg-gray-50"
                >
                  ✕ סגור
                </button>
              </div>
            </div>
            {names.map((n) => (
              <label key={n.name}
                     className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={picked.includes(n.name)}
                       onChange={() => toggle(n.name)} className="accent-blue-600" />
                <span className="text-xs text-gray-800">{n.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
